import clsx from 'clsx';
import parse from 'html-react-parser';
import { escape } from 'lodash';
import moment from 'moment';
import React, { ReactNode, useCallback } from 'react';

import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import { ShadowRoot } from '@/src/components/common/Elements/ShadowRoot/ShadowRoot';
import { getEmailContentPreview } from '@/src/components/project/Inbox2/components/Sidebar/InboxEntry/EmailThreadEntry/EmailThreadEntry';
import AttachmentsCount from '@/src/components/project/Inbox2/Elements/Buttons/AttachmentsCount/AttachmentsCount';
import EntryCard from '@/src/components/project/Inbox2/Elements/EntryCard/EntryCard';
import { replaceImageWithIconPlaceHolder } from '@/src/components/project/Inbox2/Helper';
import { getOpenInboundAttachmentsCount } from '@/src/components/project/Inbox2/models/EmailEntry';
import { EmailEntryListItem, EmailEntryType } from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { DEFAULT_DATE_FORMAT } from '@/src/config';

import { useEmailThreadEntryViewer } from '../../../../store/emailThreadEntryViewer.store';
import ActiveFilesBadge from '../../../Attachments/ActiveFilesBadge/ActiveFilesBadge';
import UpdateEmailStatus from '../../../UpdateEmailStatus/UpdateEmailStatus';
import {
  getEmailEntryToUpdateStatus,
  getEmailTypeDisplay,
  getUserDisplay,
} from '../EmailEntryExpanded/EmailEntryExpanded';

export const DATE_FORMAT = `${DEFAULT_DATE_FORMAT} h:mm A`;

type EmailEntrySummaryProps = {
  children?: ReactNode;
  emailEntryListItem: EmailEntryListItem;
  onClick?: (event: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => void;
};

const EmailEntrySummary = ({ children, emailEntryListItem, onClick }: EmailEntrySummaryProps) => {
  const { selectedEmailEntry, setSelectedEmailEntryListItem } = useEmailThreadEntryViewer();
  const { isTrash } = useInboxSearchParams();
  const dateReceived = moment(emailEntryListItem.dateAdded).format(DATE_FORMAT);
  const dateReceivedFromNow = moment(dateReceived).fromNow();
  const messageText = getEmailContentPreview(emailEntryListItem.text);
  const messageSummary = emailEntryListItem.text
    ? parse(replaceImageWithIconPlaceHolder(escape(messageText)))
    : null;

  const userDisplay = getUserDisplay(emailEntryListItem.from);
  const emailEntryToUpdateStatus = getEmailEntryToUpdateStatus(emailEntryListItem);
  const openInboundAttachmentsCount = getOpenInboundAttachmentsCount(emailEntryListItem.files.list);
  const emailTypeDisplay = getEmailTypeDisplay(emailEntryListItem.emailType);
  const isCurrentActive = selectedEmailEntry?.id === emailEntryListItem.id;
  const isSentEmailType = emailEntryListItem.emailType === EmailEntryType.SENT;

  const handleSelect = useCallback(
    (e) => {
      setSelectedEmailEntryListItem(emailEntryListItem);
      onClick(e);
    },
    [emailEntryListItem, onClick, setSelectedEmailEntryListItem]
  );

  return (
    <React.Fragment>
      <EntryCard
        // cardRef={hoverRef}
        className={clsx(
          'container--emailSummary',
          'tw-rounded-[5px]',
          'tw-p-[10px] tw-mb-[10px]',
          'hover:tw-shadow-[1px_2px_4px_0px_rgba(80,129,255,0.20)]',
          emailEntryListItem.isRead && 'tw-bg-cfo-grey-100'
        )}
        isActive={isCurrentActive}
      >
        {/* added to incorporate the section loader */}
        {children}

        <div
          className="tw-cursor-pointer"
          onClick={handleSelect}
          onKeyDown={handleSelect}
          tabIndex={0}
          role="button"
        >
          {/* Email Header ( Avatar, Name, Date Received) */}
          <div className={clsx('tw-flex tw-items-center tw-justify-between', 'tw-pb-[10px]')}>
            <div className={clsx('tw-flex tw-items-center')}>
              {userDisplay}
              {/* <p className={clsx('tw-font-brandonTextMedium tw-text-cfo-black', 'tw-ml-[10px]')}>{emailEntrySummary.emailFrom.email}</p> */}
            </div>

            <div className={clsx('tw-flex tw-items-center tw-gap-1')}>
              {isSentEmailType && (
                <Icon
                  icon={ICONS.outbound({
                    height: '24',
                    width: '24',
                    className: 'tw-text-cfo-grey-600',
                  })}
                />
              )}
              <p className={clsx('tw-p-0', 'tw-text-cfo-black')}>
                {emailTypeDisplay} {dateReceived} ({dateReceivedFromNow})
              </p>
            </div>
          </div>

          {/* Email Status */}
          {!emailEntryListItem.isDeleted && emailEntryToUpdateStatus && (
            <div className={clsx('tw-flex tw-items-center tw-justify-end')}>
              <UpdateEmailStatus emailEntry={emailEntryToUpdateStatus} />
            </div>
          )}

          {/* Email Summary */}
          <div className={clsx('tw-pl-[40px] tw-my-[5px] tw-line-clamp-1')}>
            <ShadowRoot
              htmlView={messageSummary}
              styleTagContent={`
                * {
                  color: #414A4F;
                  font-size: 14px;
                }
              `}
            />
          </div>

          {/* Email Attachments Count */}
          <div
            className={clsx(
              'container--emailAttachments',
              'tw-w-full',
              'tw-flex tw-items-center',
              'tw-border-solid tw-border-t-[1px] tw-border-t-cfo-grey-500',
              'tw-p-[10px] tw-mt-[10px]',
              emailEntryListItem.files.total <= 0 && 'tw-hidden'
            )}
          >
            {!!emailEntryListItem.files.total && (
              <AttachmentsCount
                count={emailEntryListItem.files.total}
                className={clsx(
                  'tw-px-[10px] tw-py-[5px]',
                  'tw-rounded-[34px] ',
                  'tw-bg-cfo-grey-100'
                )}
                onClick={() => {}}
              />
            )}
            {!isTrash && !isSentEmailType && (
              <ActiveFilesBadge count={openInboundAttachmentsCount} />
            )}
          </div>
        </div>
      </EntryCard>
    </React.Fragment>
  );
};

export default EmailEntrySummary;
